/**
 * Utility module for modern Selection API and Range API operations
 * Replaces deprecated document.execCommand() usage
 */

/**
 * Named CSS colors that are supported for table border color extraction
 */
const NAMED_COLORS = {
  black: '#000000',
  white: '#ffffff',
  red: '#ff0000',
  green: '#008000',
  blue: '#0000ff',
  yellow: '#ffff00',
  orange: '#ffa500',
  purple: '#800080',
  pink: '#ffc0cb',
  gray: '#808080',
  grey: '#808080',
  silver: '#c0c0c0',
  gold: '#ffd700',
  brown: '#a52a2a',
  cyan: '#00ffff',
  magenta: '#ff00ff',
  lime: '#00ff00',
  navy: '#000080',
  teal: '#008080',
  olive: '#808000',
  maroon: '#800000',
  aqua: '#00ffff',
  fuchsia: '#ff00ff',
};

/**
 * Get the current selection object
 * @returns {Selection|null} The current selection
 */
export function getSelection() {
  return window.getSelection();
}

/**
 * Save the current selection state
 * @returns {Object} Object containing range and selection info
 */
export function saveSelection() {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  return {
    range: selection.getRangeAt(0).cloneRange(),
    rangeCount: selection.rangeCount,
  };
}

/**
 * Restore a previously saved selection
 * @param {Object} savedSelection - The saved selection object
 */
export function restoreSelection(savedSelection) {
  if (!savedSelection || !savedSelection.range) {
    return;
  }

  const selection = getSelection();
  selection.removeAllRanges();
  selection.addRange(savedSelection.range);
}

/**
 * Apply color styles to math elements and their children
 * @param {Element} container - The container element to search within
 * @param {string} colorStyle - The color style to apply (e.g., 'color: red;')
 */
function applyColorToMathElements(container, colorStyle) {
  // Extract the color value from the style string
  const colorMatch = colorStyle.match(/color:\s*([^;]+)/);
  if (!colorMatch) {
    return;
  }
  const colorValue = colorMatch[1].trim();

  // Find all KaTeX math elements
  const katexElements = container.querySelectorAll('.quillkit-math, .katex, .katex-html');
  katexElements.forEach((mathEl) => {
    // Apply color to all descendant elements that render text
    const textElements = mathEl.querySelectorAll('*');
    textElements.forEach((el) => {
      // Use setAttribute for better compatibility with MathML elements
      const currentStyle = el.getAttribute('style') || '';
      const newStyle = currentStyle
        ? `${currentStyle}; color: ${colorValue};`
        : `color: ${colorValue};`;
      el.setAttribute('style', newStyle);
    });
    // Also apply to the math element itself
    const currentStyle = mathEl.getAttribute('style') || '';
    const newStyle = currentStyle
      ? `${currentStyle}; color: ${colorValue};`
      : `color: ${colorValue};`;
    mathEl.setAttribute('style', newStyle);
  });

  // Find all MathML elements and apply mathcolor attribute
  const mathMLElements = container.querySelectorAll('math, math *');
  mathMLElements.forEach((mathEl) => {
    // MathML elements should use mathcolor attribute for proper color rendering
    mathEl.setAttribute('mathcolor', colorValue);
    // Also set CSS style for broader compatibility
    const currentStyle = mathEl.getAttribute('style') || '';
    const newStyle = currentStyle
      ? `${currentStyle}; color: ${colorValue};`
      : `color: ${colorValue};`;
    mathEl.setAttribute('style', newStyle);
  });
}

/**
 * Parse a CSS style string into a property map
 * @param {string} styleString - The style string to parse
 * @returns {Object} Map of CSS properties to values
 */
function parseStyleString(styleString) {
  const styleMap = {};
  if (styleString) {
    const styles = styleString.split(';').filter((s) => s.trim());
    styles.forEach((style) => {
      const [property, value] = style.split(':').map((s) => s.trim());
      if (property && value) {
        styleMap[property.toLowerCase()] = value;
      }
    });
  }
  return styleMap;
}

/**
 * Update a style attribute by merging/replacing properties
 * @param {string} existingStyle - The existing style attribute value
 * @param {string} newStyle - The new style to apply
 * @returns {string} The merged style attribute value
 */
function updateStyleAttribute(existingStyle, newStyle) {
  // Parse existing and new styles
  const styleMap = parseStyleString(existingStyle);
  const newStyleMap = parseStyleString(newStyle);

  // Merge new styles into existing (overriding existing properties)
  Object.assign(styleMap, newStyleMap);

  // Rebuild the style string
  const entries = Object.entries(styleMap);
  if (entries.length === 0) {
    return '';
  }

  // Return with trailing semicolon for consistency with standard CSS formatting
  return entries.map(([prop, val]) => `${prop}: ${val}`).join('; ') + ';';
}

/**
 * Wrap selected content with a tag
 * @param {string} tagName - The tag name to wrap with (e.g., 'b', 'i', 'u')
 * @param {Object} attributes - Optional attributes to apply (e.g., { style: 'color: red;' })
 */
export function wrapSelection(tagName, attributes = {}) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);

  // Check if selection is already wrapped with this tag
  let ancestor = range.commonAncestorContainer;
  if (ancestor.nodeType === Node.TEXT_NODE) {
    ancestor = ancestor.parentNode;
  }

  // Special case: if commonAncestorContainer is an inline formatting element,
  // but range.startContainer and range.endContainer are text nodes inside it,
  // use the text node's parent for ancestor instead
  if (ancestor.nodeType === Node.ELEMENT_NODE &&
      range.startContainer.nodeType === Node.TEXT_NODE &&
      range.endContainer.nodeType === Node.TEXT_NODE &&
      range.startContainer.parentNode === range.endContainer.parentNode) {
    // Both start and end are in the same text node or adjacent text nodes
    // Use that text node's parent as ancestor
    ancestor = range.startContainer.parentNode;
  }

  // Check if already wrapped (only for simple tags without attributes)
  if (!attributes || Object.keys(attributes).length === 0) {
    // First, check if the commonAncestorContainer itself is the target tag
    if (ancestor.tagName && ancestor.tagName.toLowerCase() === tagName.toLowerCase()) {
      // Unwrap by replacing the tag with its contents
      const parent = ancestor.parentNode;
      while (ancestor.firstChild) {
        parent.insertBefore(ancestor.firstChild, ancestor);
      }
      parent.removeChild(ancestor);
      return;
    }

    // Check if parent is already wrapped
    let currentNode = ancestor;
    while (currentNode && currentNode.tagName) {
      if (currentNode.tagName.toLowerCase() === tagName.toLowerCase()) {
        // Already wrapped, unwrap it
        const parent = currentNode.parentNode;
        while (currentNode.firstChild) {
          parent.insertBefore(currentNode.firstChild, currentNode);
        }
        parent.removeChild(currentNode);
        return;
      }
      if (currentNode.classList && currentNode.classList.contains('quillkit-preview')) {
        break;
      }
      currentNode = currentNode.parentNode;
    }

    // New check: if the selection contains exactly one element of the target type,
    // and the selection spans the entire content of that element, unwrap it
    if (ancestor.nodeType === Node.ELEMENT_NODE) {
      const targetElements = Array.from(ancestor.getElementsByTagName(tagName));

      // Check if there's exactly one target element
      if (targetElements.length === 1) {
        const targetElement = targetElements[0];

        // Create a test range that selects the entire contents of the target element
        const testRange = document.createRange();
        testRange.selectNodeContents(targetElement);

        // Check if the current selection matches the target element's content
        const selectionMatchesTarget =
          range.compareBoundaryPoints(Range.START_TO_START, testRange) === 0 &&
          range.compareBoundaryPoints(Range.END_TO_END, testRange) === 0;

        if (selectionMatchesTarget) {
          // Unwrap the target element
          const parent = targetElement.parentNode;
          while (targetElement.firstChild) {
            parent.insertBefore(targetElement.firstChild, targetElement);
          }
          parent.removeChild(targetElement);
          return;
        }
      }
    }
  }

  // Special handling for MathML elements to prevent content loss
  // Check if the selection contains or is within a MathML element
  let mathElement = null;
  let node = ancestor;

  // Walk up the tree to find a MathML element
  while (node && node !== document && !node.classList?.contains('quillkit-preview')) {
    // MathML elements use lowercase tagName in JSDOM and some browsers
    if (node.tagName && node.tagName.toUpperCase() === 'MATH') {
      mathElement = node;
      break;
    }
    node = node.parentNode;
  }

  // If we found a MathML element and we're selecting content inside it
  if (mathElement) {
    // Check if the selection is contained entirely within the math element
    const rangeIntersectsMath = range.intersectsNode(mathElement);

    if (rangeIntersectsMath) {
      // Instead of extracting content (which can break MathML structure),
      // we'll wrap the entire math element
      const wrapper = document.createElement(tagName);

      // Apply attributes if provided
      if (attributes) {
        Object.keys(attributes).forEach((attr) => {
          if (attr === 'style') {
            wrapper.setAttribute('style', attributes[attr]);
          } else {
            wrapper.setAttribute(attr, attributes[attr]);
          }
        });
      }

      // Insert wrapper before the math element
      mathElement.parentNode.insertBefore(wrapper, mathElement);
      // Move math element into wrapper
      wrapper.appendChild(mathElement);

      // If we're applying a color style, also apply it to the math elements
      if (attributes && attributes.style && attributes.style.includes('color:')) {
        applyColorToMathElements(wrapper, attributes.style);
      }

      // Restore selection around the new wrapper
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNode(wrapper);
      selection.addRange(newRange);
      return;
    }
  }

  // Special handling for style attributes to avoid unnecessary nesting
  // If we're applying a style attribute and the selection is entirely within
  // a single element of the same tag with a style attribute, update it instead
  if (attributes && attributes.style && tagName.toLowerCase() === 'span') {
    // Check if the range encompasses the entire contents of a single span
    const startContainer = range.startContainer;
    const endContainer = range.endContainer;

    // Get the parent span if we're selecting text nodes
    let targetSpan = null;
    if (startContainer === endContainer && startContainer.nodeType === Node.TEXT_NODE) {
      const parent = startContainer.parentNode;
      if (parent && parent.tagName && parent.tagName.toLowerCase() === 'span') {
        targetSpan = parent;
      }
    } else if (ancestor.tagName && ancestor.tagName.toLowerCase() === 'span') {
      // Check if we're selecting all contents of the ancestor span
      targetSpan = ancestor;
    }

    // If we found a target span and we're selecting all its contents
    if (targetSpan) {
      const rangeStartOffset = range.startOffset;
      const rangeEndOffset = range.endOffset;

      // Check if we're selecting the entire content
      let isFullSelection = false;
      if (startContainer === endContainer) {
        if (startContainer.nodeType === Node.TEXT_NODE) {
          // Selecting entire text node
          isFullSelection = rangeStartOffset === 0 && rangeEndOffset === startContainer.length;
        } else if (startContainer === targetSpan) {
          // Selecting all child nodes
          isFullSelection =
            rangeStartOffset === 0 && rangeEndOffset === targetSpan.childNodes.length;
        }
      }

      // Also check if range selects all content by comparing with selectNodeContents
      if (!isFullSelection) {
        const testRange = document.createRange();
        testRange.selectNodeContents(targetSpan);
        isFullSelection =
          range.compareBoundaryPoints(Range.START_TO_START, testRange) === 0 &&
          range.compareBoundaryPoints(Range.END_TO_END, testRange) === 0;
      }

      if (isFullSelection && targetSpan.hasAttribute('style')) {
        // Parse and update the style attribute
        const newStyle = updateStyleAttribute(targetSpan.getAttribute('style'), attributes.style);
        targetSpan.setAttribute('style', newStyle);

        // Restore selection
        selection.removeAllRanges();
        const newRange = document.createRange();
        newRange.selectNodeContents(targetSpan);
        selection.addRange(newRange);
        return;
      }
    }
  }

  // Check for conflicting formatting before extracting
  if (!attributes || Object.keys(attributes).length === 0) {
    // Check if ALL content in the range is already formatted with the target tag
    // This is the correct toggle behavior: only remove formatting if everything is formatted
    let allContentIsFormatted = false;
    if (ancestor.nodeType === Node.ELEMENT_NODE) {
      // Extract a clone of the range contents to check
      const clonedContents = range.cloneContents();
      
      // Get all text nodes in the cloned contents
      const textNodes = [];
      const acceptNode = (node) => {
        // Only accept text nodes with non-whitespace content
        return node.textContent.trim().length > 0
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      };
      const walker = document.createTreeWalker(
        clonedContents,
        NodeFilter.SHOW_TEXT,
        acceptNode
      );
      
      let node;
      while ((node = walker.nextNode())) {
        textNodes.push(node);
      }
      
      // If we have text nodes, check if ALL of them are inside the target tag
      if (textNodes.length > 0) {
        allContentIsFormatted = textNodes.every(textNode => {
          let parent = textNode.parentNode;
          while (parent && parent !== clonedContents) {
            if (parent.tagName && parent.tagName.toLowerCase() === tagName.toLowerCase()) {
              return true;
            }
            parent = parent.parentNode;
          }
          return false;
        });
      }
    }
    
    // Only remove formatting if ALL content is formatted
    if (allContentIsFormatted) {
      const extractedFragment = range.extractContents();
      const matchingElements = extractedFragment.querySelectorAll(tagName);
      
      Array.from(matchingElements).forEach(elem => {
        const parentNode = elem.parentNode;
        while (elem.firstChild) {
          parentNode.insertBefore(elem.firstChild, elem);
        }
        parentNode.removeChild(elem);
      });
      
      range.insertNode(extractedFragment);
      
      // Restore selection - store child nodes before they're moved
      const insertedNodes = [];
      let currentNode = range.startContainer;
      if (currentNode.nodeType === Node.TEXT_NODE) {
        currentNode = currentNode.parentNode;
      }
      // Find the nodes we just inserted
      for (let i = 0; i < currentNode.childNodes.length; i++) {
        insertedNodes.push(currentNode.childNodes[i]);
      }
      
      const sel = window.getSelection();
      if (sel && insertedNodes.length > 0) {
        sel.removeAllRanges();
        const newRange = document.createRange();
        newRange.setStartBefore(insertedNodes[0]);
        newRange.setEndAfter(insertedNodes[insertedNodes.length - 1]);
        sel.addRange(newRange);
      }
      return;
    }
  }

  // Extract contents and wrap
  const wrapper = document.createElement(tagName);
  // Apply attributes if provided
  if (attributes) {
    Object.keys(attributes).forEach((attr) => {
      if (attr === 'style') {
        wrapper.setAttribute('style', attributes[attr]);
      } else {
        wrapper.setAttribute(attr, attributes[attr]);
      }
    });
  }

  // Remember where we're extracting from
  const startContainer = range.startContainer;
  const parentBeforeExtract = startContainer.nodeType === Node.TEXT_NODE
    ? startContainer.parentNode
    : startContainer;

  // Extract the selection contents
  const fragment = range.extractContents();

  // Handle wrapping based on what was extracted and what was left behind
  const inlineFormattingTags = ['b', 'i', 'u', 's', 'em', 'strong', 'span', 'mark'];

  // Check if fragment contains existing formatting of the same type
  // If so, we need to flatten it before wrapping everything
  if (!attributes || Object.keys(attributes).length === 0) {
    const existingFormatting = fragment.querySelectorAll(tagName);
    if (existingFormatting.length > 0) {
      // Mixed formatting scenario: fragment has some formatted, some not
      // Remove the existing formatting tags and then wrap everything
      Array.from(existingFormatting).forEach(elem => {
        const parentNode = elem.parentNode;
        while (elem.firstChild) {
          parentNode.insertBefore(elem.firstChild, elem);
        }
        parentNode.removeChild(elem);
      });
      // Now wrap everything in the new tag
      wrapper.appendChild(fragment);
      range.insertNode(wrapper);
      
      // Select the contents of the newly inserted wrapper
      const selectionAfter = getSelection();
      if (selectionAfter) {
        selectionAfter.removeAllRanges();
        const selectRange = document.createRange();
        selectRange.selectNodeContents(wrapper);
        selectionAfter.addRange(selectRange);
      }
      return;
    }
  }

  // Case 1: Fragment contains a single inline formatting element
  if (fragment.childNodes.length === 1 &&
      fragment.firstChild.nodeType === Node.ELEMENT_NODE &&
      inlineFormattingTags.includes(fragment.firstChild.tagName?.toLowerCase()) &&
      fragment.firstChild.tagName.toLowerCase() !== tagName.toLowerCase()) {
    const extractedElement = fragment.firstChild;

    // Check if we left an empty parent element that should also be included
    if (parentBeforeExtract &&
        parentBeforeExtract.nodeType === Node.ELEMENT_NODE &&
        inlineFormattingTags.includes(parentBeforeExtract.tagName?.toLowerCase()) &&
        parentBeforeExtract.tagName.toLowerCase() !== tagName.toLowerCase() &&
        parentBeforeExtract.childNodes.length === 0 &&
        parentBeforeExtract.isConnected) {
      // We have an extracted element and an empty parent
      // Put the extracted element back into the empty parent, then wrap both
      // This creates proper nesting like <u><i><b>text</b></i></u>
      const emptyParent = parentBeforeExtract;
      emptyParent.parentNode.removeChild(emptyParent);
      emptyParent.appendChild(extractedElement);
      wrapper.appendChild(emptyParent);
    } else {
      // No empty parent - just wrap the extracted element
      wrapper.appendChild(extractedElement);
    }

    range.insertNode(wrapper);
  }
  // Case 2: Empty inline formatting element left in DOM
  else if (parentBeforeExtract &&
           parentBeforeExtract.nodeType === Node.ELEMENT_NODE &&
           inlineFormattingTags.includes(parentBeforeExtract.tagName?.toLowerCase()) &&
           parentBeforeExtract.tagName.toLowerCase() !== tagName.toLowerCase() &&
           parentBeforeExtract.childNodes.length === 0 &&
           parentBeforeExtract.isConnected) {
    // We extracted text content from inside a formatting element, leaving it empty
    // Decision: should we insert INTO parent or WRAP it?
    // After analysis: italic (i) + bold (b) → b < i → should INSERT bold into italic
    // bold (b) + italic (i) → b < i → should WRAP bold with italic
    // So: if parentTag > newTag, INSERT. If parentTag < newTag, WRAP.
    const parentTag = parentBeforeExtract.tagName.toLowerCase();
    const shouldInsert = parentTag > tagName.toLowerCase();

    if (shouldInsert) {
      // INSERT: new tag goes inside parent (parent tag alphabetically after new tag)
      wrapper.appendChild(fragment);
      parentBeforeExtract.appendChild(wrapper);
    } else {
      // WRAP: new tag wraps parent (parent tag alphabetically before new tag)
      const emptyElement = parentBeforeExtract;
      emptyElement.parentNode.removeChild(emptyElement);
      emptyElement.appendChild(fragment);
      wrapper.appendChild(emptyElement);
      range.insertNode(wrapper);
    }
  }
  // Case 3: Normal case
  else {
    wrapper.appendChild(fragment);
    range.insertNode(wrapper);
  }

  // Select the contents of the newly inserted wrapper
  const selectionAfter = getSelection();
  if (selectionAfter) {
    selectionAfter.removeAllRanges();
    const selectRange = document.createRange();
    selectRange.selectNodeContents(wrapper);
    selectionAfter.addRange(selectRange);
  }

  // If we're applying a color style, also apply it to any math elements
  if (attributes && attributes.style && attributes.style.includes('color:')) {
    applyColorToMathElements(wrapper, attributes.style);
  }
}

/**
 * Apply a CSS style to the selected text by wrapping in a span
 * @param {string} property - CSS property name
 * @param {string} value - CSS property value
 */
export function applyStyle(property, value) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);
  const fragment = range.extractContents();
  const span = document.createElement('span');
  span.style[property] = value;
  span.appendChild(fragment);
  insertHTML(span.outerHTML);
  // Restore selection
  const selectionAfter = getSelection();
  if (selectionAfter && selectionAfter.rangeCount > 0) {
    const newRange = selectionAfter.getRangeAt(0);
    let node = newRange.commonAncestorContainer;
    if (node.nodeType !== Node.ELEMENT_NODE) node = node.parentElement;
    while (node && node.tagName?.toLowerCase() !== 'span') {
      node = node.parentElement;
    }
    if (node) {
      selectionAfter.removeAllRanges();
      const rangeSpan = document.createRange();
      rangeSpan.selectNodeContents(node);
      selectionAfter.addRange(rangeSpan);
    }
  }
}

/**
 * Format selected text as a block element
 * @param {string} tagName - Block tag name (e.g., 'h1', 'h2', 'p')
 */
export function formatBlock(tagName) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);
  let blockElement = range.commonAncestorContainer;

  // Find the block-level parent
  while (blockElement && blockElement.nodeType !== Node.ELEMENT_NODE) {
    blockElement = blockElement.parentNode;
  }

  while (blockElement && !isBlockElement(blockElement)) {
    blockElement = blockElement.parentNode;
  }

  if (!blockElement || blockElement.classList.contains('quillkit-preview')) {
    // No suitable block found, wrap selection in new block using insertHTML
    const fragment = range.extractContents();
    const newBlock = document.createElement(tagName);
    newBlock.appendChild(fragment);
    insertHTML(newBlock.outerHTML);
    // Restore selection
    const selectionAfter = getSelection();
    if (selectionAfter && selectionAfter.rangeCount > 0) {
      const newRange = selectionAfter.getRangeAt(0);
      let node = newRange.commonAncestorContainer;
      if (node.nodeType !== Node.ELEMENT_NODE) node = node.parentElement;
      while (node && node.tagName?.toLowerCase() !== tagName.toLowerCase()) {
        node = node.parentElement;
      }
      if (node) {
        selectionAfter.removeAllRanges();
        const rangeBlock = document.createRange();
        rangeBlock.selectNodeContents(node);
        selectionAfter.addRange(rangeBlock);
      }
    }
    return;
  }

  // Replace the block element
  const newBlock = document.createElement(tagName);
  while (blockElement.firstChild) {
    newBlock.appendChild(blockElement.firstChild);
  }
  blockElement.parentNode.replaceChild(newBlock, blockElement);

  // Restore selection
  selection.removeAllRanges();
  const newRange = document.createRange();
  newRange.selectNodeContents(newBlock);
  selection.addRange(newRange);
}

/**
 * Check if an element is a block-level element
 * @param {Element} element - The element to check
 * @returns {boolean} True if block-level
 */
function isBlockElement(element) {
  if (!element || !element.tagName) {
    return false;
  }

  // Check computed display style for more accurate detection
  const display = window.getComputedStyle(element).display;
  return display === 'block' || display === 'list-item' || display === 'table';
}

/**
 * Get all block elements that are fully or partially within a range
 * @param {Range} range - The range to search within
 * @returns {Array<Element>} Array of block elements in the range
 */
function getBlockElementsInRange(range) {
  const blocks = [];

  // Get the common ancestor container
  let ancestor = range.commonAncestorContainer;
  if (ancestor.nodeType !== Node.ELEMENT_NODE) {
    ancestor = ancestor.parentElement;
  }

  // If the common ancestor is the editor container, get all children
  if (ancestor.classList?.contains('quillkit-preview')) {
    const children = Array.from(ancestor.children);
    children.forEach((child) => {
      if (isBlockElement(child) && rangeIntersectsNode(range, child)) {
        blocks.push(child);
      }
    });
    return blocks;
  }

  // Find all block descendants within the range
  const walker = document.createTreeWalker(
    ancestor,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (node) => {
        if (isBlockElement(node) &&
            !node.classList?.contains('quillkit-preview') &&
            rangeIntersectsNode(range, node)) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
      }
    }
  );

  let node;
  while ((node = walker.nextNode())) {
    // Make sure this block is not a child of another block we already added
    const isNestedInExistingBlock = blocks.some(existingBlock =>
      existingBlock.contains(node) && existingBlock !== node
    );

    if (!isNestedInExistingBlock) {
      blocks.push(node);
    }
  }

  // If we found no blocks, check if the ancestor itself is a block
  if (blocks.length === 0 && isBlockElement(ancestor) && !ancestor.classList?.contains('quillkit-preview')) {
    blocks.push(ancestor);
  }

  return blocks;
}

/**
 * Check if a range intersects with a node
 * @param {Range} range - The range to check
 * @param {Node} node - The node to check
 * @returns {boolean} True if the range intersects the node
 */
function rangeIntersectsNode(range, node) {
  // Validate node parameter
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  const nodeRange = document.createRange();
  nodeRange.selectNode(node);

  // Check if ranges overlap
  return range.compareBoundaryPoints(Range.END_TO_START, nodeRange) <= 0 &&
         range.compareBoundaryPoints(Range.START_TO_END, nodeRange) >= 0;
}

/**
 * Insert HTML at the current cursor position
 * @param {string} html - The HTML string to insert
 *
 * SECURITY WARNING: This function inserts HTML without sanitization.
 * If accepting HTML from untrusted sources, sanitize it first using a library
 * like DOMPurify to prevent XSS attacks.
 */
export function insertHTML(html) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);
  range.deleteContents();

  const template = document.createElement('template');
  template.innerHTML = html.trim();
  const fragment = template.content;

  let lastNode = null;
  const nodes = Array.from(fragment.childNodes);

  nodes.forEach((node) => {
    range.insertNode(node);
    lastNode = node;
    range.setStartAfter(lastNode);
    range.collapse(true);
  });

  if (lastNode) {
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

/**
 * Create a link from the selected text
 * @param {string} url - The URL for the link
 */
export function createLink(url) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);
  const selectedText = range.toString();

  if (!selectedText) {
    // No text selected, insert link with URL as text
    const link = document.createElement('a');
    link.href = url;
    link.textContent = url;
    range.insertNode(link);

    // Position cursor after link
    range.setStartAfter(link);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    // Wrap selected text in link
    const fragment = range.extractContents();
    const link = document.createElement('a');
    link.href = url;
    link.appendChild(fragment);
    range.insertNode(link);

    // Select the link
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(link);
    selection.addRange(newRange);
  }
}

/**
 * Insert an image at the cursor position
 * @param {string} src - The image source URL
 * @param {string} alt - Optional alt text for accessibility (defaults to empty string)
 * @param {number} width - Optional width in pixels
 * @param {number} height - Optional height in pixels
 */
export function insertImage(src, alt = '', width = null, height = null) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const img = document.createElement('img');
  img.src = src;
  img.alt = alt || src.split('/').pop() || 'Image';
  img.className = 'quillkit-image';
  // Set dimensions if provided
  if (width && width > 0) {
    img.style.width = width + 'px';
  }
  if (height && height > 0) {
    img.style.height = height + 'px';
  }
  // Note: insertHTML will handle deleteContents(), so we don't call it here
  insertHTML(img.outerHTML);
  // Restore selection after image
  const selectionAfter = getSelection();
  if (selectionAfter && selectionAfter.rangeCount > 0) {
    const newRange = selectionAfter.getRangeAt(0);
    let node = newRange.commonAncestorContainer;
    if (node.nodeType !== Node.ELEMENT_NODE) node = node.parentElement;
    while (node && node.tagName?.toLowerCase() !== 'img') {
      node = node.parentElement;
    }
    if (node) {
      selectionAfter.removeAllRanges();
      const rangeImg = document.createRange();
      rangeImg.setStartAfter(node);
      rangeImg.collapse(true);
      selectionAfter.addRange(rangeImg);
    }
  }
}

/**
 * Toggle a list (ordered or unordered) for the selected block
 * @param {string} listType - 'ul' for unordered, 'ol' for ordered
 * @param {boolean} forceRemove - If true, always remove the list; if false/undefined, toggle
 */
export function toggleList(listType, forceRemove = false) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);
  let currentElement = range.commonAncestorContainer;

  // Find closest block element
  while (currentElement && currentElement.nodeType !== Node.ELEMENT_NODE) {
    currentElement = currentElement.parentNode;
  }

  // Check if we're already in a list
  let listElement = currentElement;
  while (listElement && listElement.tagName !== 'UL' && listElement.tagName !== 'OL') {
    if (listElement.classList && listElement.classList.contains('quillkit-preview')) {
      listElement = null;
      break;
    }
    listElement = listElement.parentNode;
  }

  if (listElement && (listElement.tagName === 'UL' || listElement.tagName === 'OL')) {
    // We're in a list, remove it (either forced or toggled)
    const items = Array.from(listElement.children);
    const parent = listElement.parentNode;

    items.forEach((item) => {
      const p = document.createElement('p');
      while (item.firstChild) {
        p.appendChild(item.firstChild);
      }
      parent.insertBefore(p, listElement);
    });

    parent.removeChild(listElement);
  } else if (!forceRemove) {
    // Create a new list only if not forcing removal
    const list = document.createElement(listType);

    // Find all block elements within the selection
    const blocksInSelection = getBlockElementsInRange(range);

    if (blocksInSelection.length === 0) {
      // No blocks found, wrap selection in a single list item
      const li = document.createElement('li');
      const fragment = range.extractContents();
      li.appendChild(fragment);
      list.appendChild(li);
      range.insertNode(list);

      // Restore selection
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(li);
      selection.addRange(newRange);
    } else {
      // Convert each block to a list item
      const firstBlock = blocksInSelection[0];
      const parent = firstBlock.parentNode;

      blocksInSelection.forEach((blockElement) => {
        const li = document.createElement('li');
        while (blockElement.firstChild) {
          li.appendChild(blockElement.firstChild);
        }
        list.appendChild(li);
      });

      // Replace the first block with the list and remove the rest
      parent.replaceChild(list, firstBlock);
      blocksInSelection.slice(1).forEach((block) => {
        if (block.parentNode) {
          block.parentNode.removeChild(block);
        }
      });

      // Restore selection to span all new list items
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(list);
      selection.addRange(newRange);
    }
  }
}

/**
 * Apply list style to the current list
 * @param {string} styleType - CSS list-style-type value (e.g., 'disc', 'circle', 'square', 'decimal', 'lower-alpha', etc.)
 */
export function applyListStyle(styleType) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);
  let currentElement = range.commonAncestorContainer;

  // Find closest element node
  while (currentElement && currentElement.nodeType !== Node.ELEMENT_NODE) {
    currentElement = currentElement.parentNode;
  }

  // Find the list element (UL or OL)
  let listElement = currentElement;
  while (listElement && listElement.tagName !== 'UL' && listElement.tagName !== 'OL') {
    if (listElement.classList && listElement.classList.contains('quillkit-preview')) {
      return; // Don't modify if we're outside the editor
    }
    listElement = listElement.parentNode;
  }

  if (listElement && (listElement.tagName === 'UL' || listElement.tagName === 'OL')) {
    // Apply the style using native CSS
    listElement.style.listStyleType = styleType;

    // For decimal-paren, also add a class as fallback for browsers that don't support @counter-style
    if (styleType === 'decimal-paren') {
      listElement.classList.add('decimal-paren-style');
    } else {
      // Remove the class if switching to a different style
      listElement.classList.remove('decimal-paren-style');
    }
  }
}

/**
 * Apply text alignment to the current block
 * @param {string} alignment - 'left', 'center', 'right', or 'justify'
 */
export function alignText(alignment) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);
  let blockElement = range.commonAncestorContainer;

  // Find the block-level parent
  while (blockElement && blockElement.nodeType !== Node.ELEMENT_NODE) {
    blockElement = blockElement.parentNode;
  }

  while (blockElement && !isBlockElement(blockElement)) {
    blockElement = blockElement.parentNode;
  }

  if (!blockElement || blockElement.classList.contains('quillkit-preview')) {
    return;
  }

  blockElement.style.textAlign = alignment;
}

/**
 * Insert a table with specified rows and columns
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of columns
 * @param {string} borderColor - Optional border color (default: '#000000')
 */
export function insertTable(rows, cols, borderColor = '#000000') {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  // Create table element
  const table = document.createElement('table');
  table.style.borderCollapse = 'collapse';
  table.style.width = '100%';
  table.dataset.borderColor = borderColor; // Store border color as data attribute

  // Create table body
  const tbody = document.createElement('tbody');

  for (let i = 0; i < rows; i++) {
    const row = document.createElement('tr');
    for (let j = 0; j < cols; j++) {
      const cell = document.createElement('td');
      cell.style.border = `1px solid ${borderColor}`;
      cell.style.padding = '8px';
      cell.textContent = '\u00A0'; // Non-breaking space
      row.appendChild(cell);
    }
    tbody.appendChild(row);
  }

  table.appendChild(tbody);

  // Wrap table in a scrollable container for horizontal overflow
  const wrapper = document.createElement('div');
  wrapper.className = 'quillkit-table-wrapper';
  wrapper.appendChild(table);

  // Insert wrapped table at cursor position using insertHTML
  // Note: insertHTML will handle deleteContents(), so we don't call it here
  insertHTML(wrapper.outerHTML);
  // Restore selection after table
  const selectionAfter = getSelection();
  if (selectionAfter && selectionAfter.rangeCount > 0) {
    const newRange = selectionAfter.getRangeAt(0);
    let node = newRange.commonAncestorContainer;
    if (node.nodeType !== Node.ELEMENT_NODE) node = node.parentElement;
    while (node && node.tagName?.toLowerCase() !== 'table') {
      node = node.parentElement;
    }
    if (node) {
      selectionAfter.removeAllRanges();
      const rangeTable = document.createRange();
      rangeTable.setStartAfter(node);
      rangeTable.collapse(true);
      selectionAfter.addRange(rangeTable);
    }
  }
}

/**
 * Add a row to a table
 * @param {HTMLTableElement} table - The table element
 * @param {number} position - Position to insert (0 = before, 1 = after)
 */
export function addTableRow(table, position = 1) {
  if (!table || table.tagName !== 'TABLE') {
    return;
  }

  const tbody = table.querySelector('tbody') || table;
  const rows = tbody.querySelectorAll('tr');

  if (rows.length === 0) {
    return;
  }

  // Get border color from table data attribute or default
  const borderColor = table.dataset.borderColor || '#ddd';

  // Find the selected cell or use first row as reference
  const selection = getSelection();
  let referenceRow = rows[0];
  let insertIndex = position === 0 ? 0 : rows.length;

  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    let node = range.commonAncestorContainer;

    // Find the cell and row
    while (node && node !== table) {
      if (node.tagName === 'TD' || node.tagName === 'TH') {
        const row = node.parentElement;
        if (row && row.tagName === 'TR') {
          referenceRow = row;
          insertIndex = Array.from(rows).indexOf(row);
          insertIndex = position === 0 ? insertIndex : insertIndex + 1;
          break;
        }
      }
      node = node.parentElement;
    }
  }

  // Create new row with same number of columns as reference
  const newRow = document.createElement('tr');
  const colCount = referenceRow.children.length;

  for (let i = 0; i < colCount; i++) {
    const cell = document.createElement('td');
    cell.style.border = `1px solid ${borderColor}`;
    cell.style.padding = '8px';
    cell.textContent = '\u00A0'; // Non-breaking space
    newRow.appendChild(cell);
  }

  // Insert the new row
  if (insertIndex >= rows.length) {
    tbody.appendChild(newRow);
  } else {
    tbody.insertBefore(newRow, rows[insertIndex]);
  }
}

/**
 * Add a column to a table
 * @param {HTMLTableElement} table - The table element
 * @param {number} position - Position to insert (0 = before, 1 = after)
 */
export function addTableColumn(table, position = 1) {
  if (!table || table.tagName !== 'TABLE') {
    return;
  }

  const tbody = table.querySelector('tbody') || table;
  const rows = tbody.querySelectorAll('tr');

  if (rows.length === 0) {
    return;
  }

  // Get border color from table data attribute or default
  const borderColor = table.dataset.borderColor || '#ddd';

  // Find the selected cell
  const selection = getSelection();
  let insertIndex = position === 0 ? 0 : rows[0].children.length;

  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    let node = range.commonAncestorContainer;

    // Find the cell
    while (node && node !== table) {
      if (node.tagName === 'TD' || node.tagName === 'TH') {
        const row = node.parentElement;
        if (row) {
          insertIndex = Array.from(row.children).indexOf(node);
          insertIndex = position === 0 ? insertIndex : insertIndex + 1;
          break;
        }
      }
      node = node.parentElement;
    }
  }

  // Add a cell to each row at the specified position
  rows.forEach((row) => {
    const newCell = document.createElement('td');
    newCell.style.border = `1px solid ${borderColor}`;
    newCell.style.padding = '8px';
    newCell.textContent = '\u00A0'; // Non-breaking space

    if (insertIndex >= row.children.length) {
      row.appendChild(newCell);
    } else {
      row.insertBefore(newCell, row.children[insertIndex]);
    }
  });
}

/**
 * Remove a row from a table
 * @param {HTMLTableElement} table - The table element
 */
export function removeTableRow(table) {
  if (!table || table.tagName !== 'TABLE') {
    return;
  }

  const tbody = table.querySelector('tbody') || table;
  const rows = tbody.querySelectorAll('tr');

  if (rows.length <= 1) {
    // Don't remove the last row
    return;
  }

  // Find the selected cell's row
  const selection = getSelection();
  let rowToRemove = null;

  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    let node = range.commonAncestorContainer;

    // Find the row
    while (node && node !== table) {
      if (node.tagName === 'TR') {
        rowToRemove = node;
        break;
      }
      node = node.parentElement;
    }
  }

  // Remove the row or the last row if none selected
  if (rowToRemove) {
    rowToRemove.remove();
  } else {
    rows[rows.length - 1].remove();
  }
}

/**
 * Remove a column from a table
 * @param {HTMLTableElement} table - The table element
 */
export function removeTableColumn(table) {
  if (!table || table.tagName !== 'TABLE') {
    return;
  }

  const tbody = table.querySelector('tbody') || table;
  const rows = tbody.querySelectorAll('tr');

  if (rows.length === 0 || rows[0].children.length <= 1) {
    // Don't remove the last column
    return;
  }

  // Find the selected cell's column index
  const selection = getSelection();
  let colIndex = rows[0].children.length - 1; // Default to last column

  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    let node = range.commonAncestorContainer;

    // Find the cell
    while (node && node !== table) {
      if (node.tagName === 'TD' || node.tagName === 'TH') {
        const row = node.parentElement;
        if (row) {
          colIndex = Array.from(row.children).indexOf(node);
          break;
        }
      }
      node = node.parentElement;
    }
  }

  // Remove the cell at colIndex from each row
  rows.forEach((row) => {
    if (row.children[colIndex]) {
      row.children[colIndex].remove();
    }
  });
}

/**
 * Remove a cell from a table row
 * @param {HTMLTableElement} table - The table element
 * @returns {void} Does nothing if no cell is selected, if the table is invalid,
 *                  or if the row has only one cell (to prevent empty rows)
 */
export function removeCell(table) {
  if (!table || table.tagName !== 'TABLE') {
    return;
  }

  const selection = getSelection();
  let cellToRemove = null;
  let rowContainingCell = null;

  // Find the selected cell
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    let node = range.commonAncestorContainer;

    // Find the cell
    while (node && node !== table) {
      if (node.tagName === 'TD' || node.tagName === 'TH') {
        cellToRemove = node;
        rowContainingCell = node.parentElement;
        break;
      }
      node = node.parentElement;
    }
  }

  // If no cell is selected or the row has only one cell, don't remove
  if (!cellToRemove || !rowContainingCell || rowContainingCell.children.length <= 1) {
    return;
  }

  // Remove the cell
  cellToRemove.remove();
}

/**
 * Delete a table
 * @param {HTMLTableElement} table - The table element
 */
export function deleteTable(table) {
  if (!table || table.tagName !== 'TABLE') {
    return;
  }

  // Check if table is wrapped in quillkit-table-wrapper
  const parent = table.parentElement;
  if (parent && parent.classList.contains('quillkit-table-wrapper')) {
    // Remove the wrapper (which contains the table)
    parent.remove();
  } else {
    // Remove just the table
    table.remove();
  }
}

/**
 * Get the table element containing the current selection
 * @returns {HTMLTableElement|null} The table element or null
 */
export function getTableAtSelection() {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  let node = range.commonAncestorContainer;

  // Handle text nodes by getting their parent element
  if (node.nodeType !== Node.ELEMENT_NODE) {
    node = node.parentElement;
  }

  // Find the table
  while (node && node.nodeType === Node.ELEMENT_NODE) {
    if (node.tagName === 'TABLE') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Normalize an existing table to work properly with editor features
 * Adds necessary attributes and styles that may be missing from tables loaded from content
 * @param {HTMLTableElement} table - The table element to normalize
 */
export function normalizeTable(table) {
  if (!table || table.tagName !== 'TABLE') {
    return;
  }

  // Apply essential table styles if not already set
  if (!table.style.borderCollapse) {
    table.style.borderCollapse = 'collapse';
  }
  if (!table.style.width) {
    table.style.width = '100%';
  }

  // Add table-layout: fixed for predictable resizing
  if (!table.style.tableLayout) {
    table.style.tableLayout = 'fixed';
  }

  // If table doesn't have a border color data attribute, try to extract it from cells
  if (!table.dataset.borderColor) {
    const tbody = table.querySelector('tbody') || table;
    const firstCell = tbody.querySelector('td, th');

    if (firstCell) {
      // Try to extract border color from the first cell's computed or inline style
      const borderColor = extractBorderColor(firstCell);
      table.dataset.borderColor = borderColor || '#ddd';
    } else {
      table.dataset.borderColor = '#ddd';
    }
  }

  // Ensure all cells have consistent styling
  const tbody = table.querySelector('tbody') || table;
  const cells = tbody.querySelectorAll('td, th');
  const borderColor = table.dataset.borderColor || '#ddd';

  cells.forEach((cell) => {
    // Only apply border if cell doesn't already have one
    if (!cell.style.border && !cell.style.borderWidth) {
      cell.style.border = `1px solid ${borderColor}`;
    }
    // Only apply padding if cell doesn't have one
    if (!cell.style.padding) {
      cell.style.padding = '8px';
    }
  });
}

/**
 * Extract border color from a cell's style
 * @param {HTMLElement} cell - The cell element
 * @returns {string|null} The extracted border color or null
 */
function extractBorderColor(cell) {
  // Check borderColor property first - it's most reliable
  if (cell.style.borderColor) {
    return convertColorToHex(cell.style.borderColor);
  }

  // Check individual border color properties
  if (cell.style.borderTopColor) {
    return convertColorToHex(cell.style.borderTopColor);
  }
  if (cell.style.borderRightColor) {
    return convertColorToHex(cell.style.borderRightColor);
  }
  if (cell.style.borderBottomColor) {
    return convertColorToHex(cell.style.borderBottomColor);
  }
  if (cell.style.borderLeftColor) {
    return convertColorToHex(cell.style.borderLeftColor);
  }

  // Check the border shorthand property - extract color from it
  if (cell.style.border) {
    // Match hex colors (#rgb or #rrggbb or #rrggbbaa)
    const hexMatch = cell.style.border.match(/#[0-9a-fA-F]{3,8}/i);
    if (hexMatch) {
      return convertColorToHex(hexMatch[0]);
    }

    // Match rgb/rgba colors
    const rgbMatch = cell.style.border.match(/rgba?\([^)]+\)/i);
    if (rgbMatch) {
      return convertColorToHex(rgbMatch[0]);
    }

    // Match named colors (must be word boundaries to avoid matching 'px' etc)
    const colorNames = Object.keys(NAMED_COLORS).join('|');
    const namedColorRegex = new RegExp(`\\b(${colorNames})\\b`, 'i');
    const namedColorMatch = cell.style.border.match(namedColorRegex);
    if (namedColorMatch) {
      return convertColorToHex(namedColorMatch[0]);
    }
  }

  // Try to get computed style (only works in browser, not in tests)
  if (typeof window !== 'undefined' && window.getComputedStyle) {
    try {
      const computed = window.getComputedStyle(cell);
      const borderColor = computed.borderColor || computed.borderTopColor;
      if (borderColor && borderColor !== 'rgba(0, 0, 0, 0)' && borderColor !== 'transparent') {
        return convertColorToHex(borderColor);
      }
    } catch (e) {
      // Ignore errors in test environments
    }
  }

  return null;
}

/**
 * Convert various color formats to hex
 * @param {string} color - The color in any CSS format
 * @returns {string} The color in hex format
 */
function convertColorToHex(color) {
  // If already hex, normalize it
  if (color.startsWith('#')) {
    const hex = color.toLowerCase();
    // Expand 3-digit hex to 6-digit hex
    if (hex.length === 4) {
      return (
        '#' +
        hex
          .slice(1)
          .split('')
          .map((c) => c + c)
          .join('')
      );
    }
    // Strip alpha channel from 8-character hex colors (#rrggbbaa -> #rrggbb)
    if (hex.length === 9) {
      return hex.substring(0, 7);
    }
    // Return 6-digit hex as-is, or truncate/pad others to 6 digits
    if (hex.length === 7) {
      return hex;
    }
    // For other lengths, return as-is (should be rare)
    return hex;
  }

  // Handle rgb/rgba - match spaces with or without them
  const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);
    return (
      '#' +
      [r, g, b]
        .map((x) => {
          const hex = x.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        })
        .join('')
    );
  }

  // Handle named colors using the constant
  const normalized = color.toLowerCase().trim();
  if (NAMED_COLORS[normalized]) {
    return NAMED_COLORS[normalized];
  }

  // If we can't convert, return a default
  return '#dddddd';
}

/**
 * Get the current font family at the cursor or selection
 * @returns {string|null} The font family or null if not found
 */
export function getCurrentFontFamily() {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  let element = range.commonAncestorContainer;

  // If it's a text node, get its parent element
  if (element.nodeType === Node.TEXT_NODE) {
    element = element.parentElement;
  }

  // Walk up the DOM tree to find font-family
  while (element && element !== document.body) {
    const computedStyle = window.getComputedStyle(element);
    const fontFamily = computedStyle.fontFamily;

    if (fontFamily && fontFamily !== 'inherit') {
      // Parse the font family to get the primary font name
      const fonts = fontFamily.split(',').map((f) => f.trim().replace(/['"]/g, ''));
      return fonts[0].toLowerCase();
    }

    element = element.parentElement;
  }

  return null;
}

/**
 * Get the current font size at the cursor or selection
 * @returns {string|null} The font size (e.g., '12pt') or null if not found
 */
export function getCurrentFontSize() {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  let element = range.commonAncestorContainer;

  // If it's a text node, get its parent element
  if (element.nodeType === Node.TEXT_NODE) {
    element = element.parentElement;
  }

  // Walk up the DOM tree to find font-size
  while (element && element !== document.body) {
    const computedStyle = window.getComputedStyle(element);
    const fontSize = computedStyle.fontSize;

    if (fontSize) {
      // Convert px to pt (1pt = 4/3px, so px to pt is px * 3/4)
      const pxValue = parseFloat(fontSize);
      const ptValue = Math.round((pxValue * 3) / 4);
      return `${ptValue}pt`;
    }

    element = element.parentElement;
  }

  return null;
}

/**
 * Get the current list style type for the active list (ul or ol)
 * @param {string} listType - 'ul' or 'ol'
 * @returns {string|null} The current list-style-type or null if not in a list of that type
 */
export function getCurrentListStyle(listType) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }
  const range = selection.getRangeAt(0);
  let currentElement = range.commonAncestorContainer;
  while (currentElement && currentElement.nodeType !== Node.ELEMENT_NODE) {
    currentElement = currentElement.parentNode;
  }
  let listElement = currentElement;
  while (
    listElement &&
    listElement.tagName !== undefined &&
    listElement.tagName !== null &&
    listElement.tagName.toLowerCase() !== listType
  ) {
    if (listElement.classList && listElement.classList.contains('quillkit-preview')) {
      return null;
    }
    listElement = listElement.parentNode;
  }
  if (listElement && listElement.tagName && listElement.tagName.toLowerCase() === listType) {
    // Return the inline style if set
    if (listElement.style.listStyleType) {
      return listElement.style.listStyleType;
    }
    // Fall back to computed style (browser default or CSS rule)
    if (typeof window !== 'undefined' && window.getComputedStyle) {
      try {
        const computedStyle = window.getComputedStyle(listElement);
        return computedStyle.listStyleType || null;
      } catch (e) {
        // Handle any errors from getComputedStyle() (e.g., in headless environments or detached elements)
        return null;
      }
    }
    return null;
  }
  return null;
}

/**
 * Check if the current selection is inside a list of the given type
 * @param {string} listType - 'ul' or 'ol'
 * @returns {boolean}
 */
export function isListActive(listType) {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  let currentElement = range.commonAncestorContainer;
  while (currentElement && currentElement.nodeType !== Node.ELEMENT_NODE) {
    currentElement = currentElement.parentNode;
  }
  let listElement = currentElement;
  while (
    listElement &&
    listElement.tagName !== undefined &&
    listElement.tagName !== null &&
    listElement.tagName.toLowerCase() !== listType
  ) {
    if (listElement.classList && listElement.classList.contains('quillkit-preview')) {
      return false;
    }
    listElement = listElement.parentNode;
  }
  return !!(listElement && listElement.tagName && listElement.tagName.toLowerCase() === listType);
}

/**
 * Get the table cell(s) containing the current selection
 * @returns {HTMLElement|HTMLElement[]|null} The cell element(s) or null
 */
export function getCellAtSelection() {
  const selection = getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  let node = range.commonAncestorContainer;

  // Handle text nodes by getting their parent element
  if (node.nodeType !== Node.ELEMENT_NODE) {
    node = node.parentElement;
  }

  // Find the cell (td or th)
  while (node && node.nodeType === Node.ELEMENT_NODE) {
    if (node.tagName === 'TD' || node.tagName === 'TH') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Get the adjacent cell in a specific direction
 * @param {HTMLElement} cell - The cell element
 * @param {string} direction - The direction ('right', 'left', 'top', 'bottom')
 * @returns {HTMLElement|null} The adjacent cell or null
 */
function getAdjacentCell(cell, direction) {
  if (!cell || (cell.tagName !== 'TD' && cell.tagName !== 'TH')) {
    return null;
  }

  const row = cell.parentElement;
  if (!row || row.tagName !== 'TR') {
    return null;
  }

  const table = row.closest('table');
  if (!table) {
    return null;
  }

  const cellIndex = Array.from(row.children).indexOf(cell);
  const rowIndex = Array.from(table.querySelectorAll('tr')).indexOf(row);

  switch (direction) {
    case 'right':
      return row.children[cellIndex + 1] || null;
    case 'left':
      return row.children[cellIndex - 1] || null;
    case 'top':
      const prevRow = table.querySelectorAll('tr')[rowIndex - 1];
      return prevRow ? prevRow.children[cellIndex] : null;
    case 'bottom':
      const nextRow = table.querySelectorAll('tr')[rowIndex + 1];
      return nextRow ? nextRow.children[cellIndex] : null;
    default:
      return null;
  }
}

/**
 * Helper function to check if a border was explicitly removed
 * @param {HTMLElement} cell - The cell element
 * @param {string} side - The side to check
 * @returns {boolean} True if the border was removed
 */
function isRemovedBorder(cell, side) {
  if (!cell.dataset.removedBorders) {
    return false;
  }
  const removed = cell.dataset.removedBorders.split(',');
  return removed.includes(side);
}

/**
 * Remove a specific border from a table cell
 * @param {HTMLElement} cell - The cell element
 * @param {string} side - The side of the border to remove ('top', 'right', 'bottom', 'left')
 */
export function removeCellBorder(cell, side) {
  if (!cell || (cell.tagName !== 'TD' && cell.tagName !== 'TH')) {
    return;
  }

  const validSides = ['top', 'right', 'bottom', 'left'];
  if (!validSides.includes(side)) {
    return;
  }

  // Preserve other border sides when removing one
  const defaultBorder = cell.style.border || '1px solid #000';
  const sides = {
    top: cell.style.borderTop || defaultBorder,
    right: cell.style.borderRight || defaultBorder,
    bottom: cell.style.borderBottom || defaultBorder,
    left: cell.style.borderLeft || defaultBorder,
  };

  // Set the target side to none
  sides[side] = 'none';

  // Clear the generic border and set individual sides
  cell.style.border = '';
  cell.style.borderTop = sides.top;
  cell.style.borderRight = sides.right;
  cell.style.borderBottom = sides.bottom;
  cell.style.borderLeft = sides.left;

  // Mark that this border was explicitly removed
  if (!cell.dataset.removedBorders) {
    cell.dataset.removedBorders = '';
  }
  const removed = cell.dataset.removedBorders.split(',').filter((s) => s);
  if (!removed.includes(side)) {
    removed.push(side);
    cell.dataset.removedBorders = removed.join(',');
  }
}

/**
 * Restore a specific border on a table cell
 * @param {HTMLElement} cell - The cell element
 * @param {string} side - The side of the border to restore ('top', 'right', 'bottom', 'left')
 */
export function restoreCellBorder(cell, side) {
  if (!cell || (cell.tagName !== 'TD' && cell.tagName !== 'TH')) {
    return;
  }

  const validSides = ['top', 'right', 'bottom', 'left'];
  if (!validSides.includes(side)) {
    return;
  }

  // Get the table's border color
  const table = getTableAtSelection();
  const borderColor = table?.dataset?.borderColor || '#000000';

  // Restore the specific border side
  const borderProperty = `border${side.charAt(0).toUpperCase()}${side.slice(1)}`;
  cell.style[borderProperty] = `1px solid ${borderColor}`;

  // Update the removed borders tracking
  if (cell.dataset.removedBorders) {
    const removed = cell.dataset.removedBorders.split(',').filter((s) => s !== side);
    cell.dataset.removedBorders = removed.join(',');
  }
}

/**
 * Toggle a specific border on a table cell
 * @param {HTMLElement} cell - The cell element
 * @param {string} side - The side of the border to toggle ('top', 'right', 'bottom', 'left')
 * @returns {boolean} True if border is now visible, false if hidden
 */
export function toggleCellBorder(cell, side) {
  if (!cell || (cell.tagName !== 'TD' && cell.tagName !== 'TH')) {
    return false;
  }

  const validSides = ['top', 'right', 'bottom', 'left'];
  if (!validSides.includes(side)) {
    return false;
  }

  // Map of sides to their opposite sides on adjacent cells
  const adjacentSideMap = {
    right: { direction: 'right', oppositeSide: 'left' },
    left: { direction: 'left', oppositeSide: 'right' },
    top: { direction: 'top', oppositeSide: 'bottom' },
    bottom: { direction: 'bottom', oppositeSide: 'top' },
  };

  // Check if border is currently visible using the visibility check function
  const isVisible = isCellBorderVisible(cell, side);

  if (isVisible) {
    removeCellBorder(cell, side);

    // Also remove the corresponding border on the adjacent cell (for inside borders)
    const adjacentInfo = adjacentSideMap[side];
    if (adjacentInfo) {
      const adjacentCell = getAdjacentCell(cell, adjacentInfo.direction);
      if (adjacentCell) {
        removeCellBorder(adjacentCell, adjacentInfo.oppositeSide);
      }
    }

    return false;
  } else {
    restoreCellBorder(cell, side);

    // Also restore the corresponding border on the adjacent cell (for inside borders)
    const adjacentInfo = adjacentSideMap[side];
    if (adjacentInfo) {
      const adjacentCell = getAdjacentCell(cell, adjacentInfo.direction);
      if (adjacentCell) {
        restoreCellBorder(adjacentCell, adjacentInfo.oppositeSide);
      }
    }

    return true;
  }
}

/**
 * Check if a specific border on a table cell is visible
 * @param {HTMLElement} cell - The cell element
 * @param {string} side - The side of the border to check ('top', 'right', 'bottom', 'left')
 * @returns {boolean} True if border is visible, false if hidden
 */
export function isCellBorderVisible(cell, side) {
  if (!cell || (cell.tagName !== 'TD' && cell.tagName !== 'TH')) {
    return false;
  }

  const validSides = ['top', 'right', 'bottom', 'left'];
  if (!validSides.includes(side)) {
    return false;
  }

  // Check if this border was explicitly removed using helper function
  if (isRemovedBorder(cell, side)) {
    return false;
  }

  const borderProperty = `border${side.charAt(0).toUpperCase()}${side.slice(1)}`;
  const currentBorder = cell.style[borderProperty];

  // If explicitly set to none, it's not visible
  if (currentBorder === 'none') {
    return false;
  }

  // If explicitly set to a value, it's visible
  if (currentBorder && currentBorder !== '') {
    return true;
  }

  // If not set individually, check the generic border property
  if (cell.style.border && cell.style.border !== 'none') {
    return true;
  }

  return false;
}

/**
 * Merge a cell with an adjacent cell in the specified direction
 * @param {HTMLElement} cell - The source cell
 * @param {string} direction - The direction ('left', 'right', 'top', 'bottom')
 * @returns {boolean} True if merge was successful
 */
export function mergeCells(cell, direction) {
  if (!cell || (cell.tagName !== 'TD' && cell.tagName !== 'TH')) {
    return false;
  }

  const adjacentCell = getAdjacentCell(cell, direction);
  if (!adjacentCell) {
    return false;
  }

  // Handle horizontal merging (left/right)
  if (direction === 'left' || direction === 'right') {
    // Determine which cell is the target (left one for both directions)
    const targetCell = direction === 'left' ? adjacentCell : cell;
    const sourceCell = direction === 'left' ? cell : adjacentCell;

    // Get current colspan values
    const targetColspan = parseInt(targetCell.getAttribute('colspan') || '1', 10);
    const sourceColspan = parseInt(sourceCell.getAttribute('colspan') || '1', 10);

    // Merge content - move DOM nodes from source to target
    if (sourceCell.childNodes.length > 0 && targetCell.childNodes.length > 0) {
      // Add a space text node between contents
      targetCell.appendChild(document.createTextNode(' '));
      // Move all child nodes from source to target
      while (sourceCell.firstChild) {
        targetCell.appendChild(sourceCell.firstChild);
      }
    } else if (sourceCell.childNodes.length > 0) {
      // Move all child nodes from source to target
      while (sourceCell.firstChild) {
        targetCell.appendChild(sourceCell.firstChild);
      }
    }

    // Set new colspan
    targetCell.setAttribute('colspan', (targetColspan + sourceColspan).toString());

    // Remove the source cell
    sourceCell.parentElement.removeChild(sourceCell);
  }
  // Handle vertical merging (top/bottom)
  else if (direction === 'top' || direction === 'bottom') {
    // Determine which cell is the target (top one for both directions)
    const targetCell = direction === 'top' ? adjacentCell : cell;
    const sourceCell = direction === 'top' ? cell : adjacentCell;

    // Get current rowspan values
    const targetRowspan = parseInt(targetCell.getAttribute('rowspan') || '1', 10);
    const sourceRowspan = parseInt(sourceCell.getAttribute('rowspan') || '1', 10);

    // Merge content - move DOM nodes from source to target
    if (sourceCell.childNodes.length > 0 && targetCell.childNodes.length > 0) {
      // Add a line break between contents
      targetCell.appendChild(document.createElement('br'));
      // Move all child nodes from source to target
      while (sourceCell.firstChild) {
        targetCell.appendChild(sourceCell.firstChild);
      }
    } else if (sourceCell.childNodes.length > 0) {
      // Move all child nodes from source to target
      while (sourceCell.firstChild) {
        targetCell.appendChild(sourceCell.firstChild);
      }
    }

    // Set new rowspan
    targetCell.setAttribute('rowspan', (targetRowspan + sourceRowspan).toString());

    // Remove the source cell
    sourceCell.parentElement.removeChild(sourceCell);
  }

  return true;
}

/**
 * Merge cell with the cell to its left
 * @param {HTMLElement} cell - The cell element
 * @returns {boolean} True if merge was successful
 */
export function mergeCellLeft(cell) {
  return mergeCells(cell, 'left');
}

/**
 * Merge cell with the cell to its right
 * @param {HTMLElement} cell - The cell element
 * @returns {boolean} True if merge was successful
 */
export function mergeCellRight(cell) {
  return mergeCells(cell, 'right');
}

/**
 * Merge cell with the cell above it
 * @param {HTMLElement} cell - The cell element
 * @returns {boolean} True if merge was successful
 */
export function mergeCellTop(cell) {
  return mergeCells(cell, 'top');
}

/**
 * Merge cell with the cell below it
 * @param {HTMLElement} cell - The cell element
 * @returns {boolean} True if merge was successful
 */
export function mergeCellBottom(cell) {
  return mergeCells(cell, 'bottom');
}

/**
 * Indent a list item
 * @param {HTMLElement} listItem 
 * @returns 
 */
export function indentListItem(listItem) {
  if (!listItem || listItem.tagName !== 'LI') {
    return;
  }

  const parentList = listItem.parentElement;
  if (!parentList || (parentList.tagName !== 'UL' && parentList.tagName !== 'OL')) {
    return;
  }

  const previousSibling = listItem.previousElementSibling;

  // Only indent if there is a previous sibling
  if (previousSibling && previousSibling.tagName === 'LI') {
    // Check for existing sublist of the same type
    let sublist = Array.from(previousSibling.children).find(
      (child) => child.tagName === parentList.tagName
    );

    // If no sublist exists, create one of the same type as the parent
    if (!sublist) {
      sublist = document.createElement(parentList.tagName);
      previousSibling.appendChild(sublist);
    }

    // Move the listItem into the sublist
    sublist.appendChild(listItem);

    // If parentList becomes empty, remove it
    if (parentList.children.length === 0) {
      parentList.parentNode.removeChild(parentList);
    }
  }
}

/**
 * Outdent a list item
 * @param {HTMLElement} listItem - The list item element
 */
export function outdentListItem(listItem) {
  if (!listItem || listItem.tagName !== 'LI') {
    return;
  }

  const parentList = listItem.parentElement;
  if (!parentList || (parentList.tagName !== 'UL' && parentList.tagName !== 'OL')) {
    return;
  }

  const grandParent = parentList.parentElement;

  // If the parent list is nested inside another list item
  if (grandParent && grandParent.tagName === 'LI') {
    const greatGrandParentList = grandParent.parentElement;

    // Move the listItem to be a sibling of the grandParent list item
    greatGrandParentList.insertBefore(listItem, grandParent.nextSibling);

    // If the parent list is now empty, remove it
    if (parentList.children.length === 0) {
      parentList.remove();
    }
  } else {
    // If the parent list is not nested, unwrap the list item to a paragraph
    const paragraph = document.createElement('p');
    paragraph.innerHTML = listItem.innerHTML;

    parentList.parentElement.insertBefore(paragraph, parentList.nextSibling);
    listItem.remove();

    // If the parent list is now empty, remove it
    if (parentList.children.length === 0) {
      parentList.remove();
    }
  }
}